import type { ReactNode } from 'react'

export type ActionSheetAction = {
  label: string
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
}

export type ActionSheetRenderContext = {
  close: () => void
  update: (input: ActionSheetUpdateInput) => void
}

export type ActionSheetSize = 'auto' | 'large'

export type ActionSheetOpenInput = {
  title?: string
  description?: string
  content?: ReactNode
  footer?: ReactNode
  render?: (context: ActionSheetRenderContext) => ReactNode
  actions?: ActionSheetAction[]
  size?: ActionSheetSize
  inset?: boolean
  dismissible?: boolean
  showHandle?: boolean
  onClose?: () => void
}

export type ActionSheetUpdateInput = Partial<
  Pick<
    ActionSheetOpenInput,
    | 'title'
    | 'description'
    | 'content'
    | 'footer'
    | 'render'
    | 'actions'
    | 'size'
    | 'inset'
    | 'dismissible'
  >
>

export type ActionSheetOptions = ActionSheetOpenInput & {
  id: number
}

type ActionSheetState = {
  open: boolean
  options: ActionSheetOptions | null
}

type Listener = () => void

let nextId = 1
let state: ActionSheetState = {
  open: false,
  options: null,
}

const listeners = new Set<Listener>()

function emit() {
  listeners.forEach((listener) => listener())
}

export function getActionSheetSnapshot(): ActionSheetState {
  return state
}

export function subscribeToActionSheet(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function normalize(input: ActionSheetOpenInput): ActionSheetOptions {
  return {
    id: nextId++,
    title: input.title,
    description: input.description,
    content: input.content,
    footer: input.footer,
    render: input.render,
    actions: input.actions,
    size: input.size ?? 'auto',
    inset: input.inset ?? false,
    dismissible: input.dismissible ?? true,
    showHandle: input.showHandle ?? true,
    onClose: input.onClose,
  }
}

export function openActionSheet(input: ActionSheetOpenInput) {
  const options = normalize(input)
  const previous = state.options
  state = {
    open: true,
    options,
  }
  emit()
  if (previous) queueMicrotask(() => previous.onClose?.())
  return options.id
}

export function updateActionSheet(input: ActionSheetUpdateInput) {
  if (!state.options) {
    return
  }
  state = {
    ...state,
    options: {
      ...state.options,
      ...input,
    },
  }
  emit()
}

export function closeActionSheet() {
  if (!state.open) {
    return
  }
  state = {
    ...state,
    open: false,
  }
  emit()
}

export function clearActionSheet() {
  const onClose = state.options?.onClose
  state = {
    open: false,
    options: null,
  }
  emit()
  onClose?.()
}

export function setActionSheetOpen(open: boolean) {
  if (open) {
    if (!state.options) {
      return
    }
    state = {
      ...state,
      open: true,
    }
    emit()
    return
  }
  closeActionSheet()
}

export function handleActionSheetOpenChange(open: boolean, id: number) {
  if (state.options?.id !== id) return
  if (open) {
    setActionSheetOpen(true)
    return
  }
  closeActionSheet()
}

export function handleActionSheetAnimationEnd(open: boolean, id: number) {
  if (open) {
    return
  }
  if (state.options?.id !== id || state.open) {
    return
  }
  const onClose = state.options.onClose
  state = {
    open: false,
    options: null,
  }
  emit()
  onClose?.()
}

export const actionSheet = {
  open: openActionSheet,
  update: updateActionSheet,
  close: closeActionSheet,
  clear: clearActionSheet,
}
